import {DatePipe} from '@angular/common';
import {Component, inject, OnInit} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {finalize} from 'rxjs';
import {NzAlertModule} from 'ng-zorro-antd/alert';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzCardModule} from 'ng-zorro-antd/card';
import {NzInputModule} from 'ng-zorro-antd/input';
import {NzPaginationModule} from 'ng-zorro-antd/pagination';
import {NzSelectModule} from 'ng-zorro-antd/select';
import {NzTableModule} from 'ng-zorro-antd/table';
import {NzTagModule} from 'ng-zorro-antd/tag';
import {
  AUTH_CLIENT_ISZH_MOBILE,
  AUTH_CLIENT_LEGACY_MOBILE,
  AUTH_CLIENT_TORT_TULIK,
  AUTH_CLIENT_WEB,
  AUTH_EVENT_LOGIN_FAILED,
  AUTH_EVENT_LOGIN_SUCCESS,
  AUTH_EVENT_LOGOUT,
  AuthFlowService,
  AuthLogRow,
} from '../../core/services/auth-flow.service';

@Component({
  selector: 'app-auth-logs',
  standalone: true,
  imports: [
    TranslatePipe,
    DatePipe,
    ReactiveFormsModule,
    RouterLink,
    NzAlertModule,
    NzButtonModule,
    NzCardModule,
    NzInputModule,
    NzPaginationModule,
    NzSelectModule,
    NzTableModule,
    NzTagModule,
  ],
  templateUrl: './auth-logs.html',
  styleUrl: './auth-logs.css'
})
export class AuthLogs implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly authFlow = inject(AuthFlowService);
  private readonly route = inject(ActivatedRoute);

  protected readonly isAdminView = this.route.snapshot.data['admin'] === true;

  readonly login = new FormControl('', {nonNullable: true});
  readonly eventType = new FormControl<number | null>(null);

  rows: AuthLogRow[] = [];
  page = 1;
  readonly pageSize = 20;
  totalElements = 0;
  isLoading = false;
  errorMessage = '';

  ngOnInit(): void {
    this.load();
  }

  applyFilters(): void {
    this.page = 1;
    this.load();
  }

  resetFilters(): void {
    this.login.reset();
    this.eventType.reset();
    this.page = 1;
    this.load();
  }

  changePage(page: number): void {
    this.page = page;
    this.load();
  }

  eventLabel(eventType: number): string {
    switch (eventType) {
      case AUTH_EVENT_LOGIN_SUCCESS:
        return this.translate.instant('authLogs.loginSuccess');
      case AUTH_EVENT_LOGIN_FAILED:
        return this.translate.instant('authLogs.loginFailed');
      case AUTH_EVENT_LOGOUT:
        return this.translate.instant('authLogs.logoutEvent');
      default:
        return this.translate.instant('authLogs.event');
    }
  }

  clientLabel(client: number): string {
    switch (client) {
      case AUTH_CLIENT_WEB:
        return 'web';
      case AUTH_CLIENT_ISZH_MOBILE:
        return 'iszh-mobile';
      case AUTH_CLIENT_TORT_TULIK:
        return 'torttulik';
      case AUTH_CLIENT_LEGACY_MOBILE:
        return 'mobile';
      default:
        return '—';
    }
  }

  eventColor(eventType: number): string {
    switch (eventType) {
      case AUTH_EVENT_LOGIN_SUCCESS:
        return 'green';
      case AUTH_EVENT_LOGIN_FAILED:
        return 'red';
      default:
        return 'default';
    }
  }

  reasonLabel(reason: string | null | undefined): string {
    switch (reason) {
      case 'invalid_credentials':
        return this.translate.instant('authLogs.reasonInvalidPsd');
      case 'user_not_found':
        return this.translate.instant('authLogs.reasonUserNotFound');
      case 'lockout':
        return this.translate.instant('authLogs.reasonLocked');
      case 'psd_expired':
        return this.translate.instant('authLogs.reasonPsdExpired');
      case 'blocked':
        return this.translate.instant('authLogs.reasonBlocked');
      case 'consent_required':
        return this.translate.instant('authLogs.reasonConsentRequired');
      case 'temp_psd':
        return this.translate.instant('authLogs.reasonTempPsd');
      case 'invalid_otp':
        return this.translate.instant('authLogs.reasonInvalidOtp');
      case 'test_login':
        return this.translate.instant('authLogs.reasonTestLogin');
      default:
        return reason ?? '';
    }
  }

  private load(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.authFlow.authLogs({
      page: this.page,
      pageSize: this.pageSize,
      login: this.isAdminView ? this.login.value : undefined,
      eventType: this.eventType.value,
    }, !this.isAdminView)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (response) => {
          this.rows = response.list;
          this.totalElements = response.totalElements;
        },
        error: (err) => {
          this.errorMessage = err?.error?.message ?? this.translate.instant('authLogs.loadError');
        }
      });
  }
}
